// src/pages/AnaKategorilerPage.js
import React, { useEffect, useMemo, useState } from "react";
import GlobalStyles from "@mui/material/GlobalStyles";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  List,
  Datagrid,
  EditButton,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRecordContext,
  useRefresh,
  useNotify,
  useRedirect,
  EditBase,
  useEditContext,
  useUpdate,
  FunctionField,
  useListContext,
} from "react-admin";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CategoryIcon from "@mui/icons-material/Category";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ImageIcon from "@mui/icons-material/Image";
import LanguageIcon from "@mui/icons-material/Language";
import SaveIcon from "@mui/icons-material/Save";
import SortIcon from "@mui/icons-material/Sort";

import ResimUpload from "../ResimUpload";
import { API_BASE } from "../config";

/* ==========================================================================
   TASARIM SABİTLERİ
   ========================================================================== */

const COLORS = {
  navy: "#0b1f3a",
  navySoft: "#15345d",
  blue: "#2563eb",
  blueSoft: "#eff6ff",
  orange: "#f59e0b",
  orangeSoft: "#fff7ed",
  green: "#10b981",
  red: "#ef4444",
  text: "#0f172a",
  textSoft: "#64748b",
  page: "#f3f6fb",
  border: "#e2e8f0",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    bgcolor: "#fff",
    transition: "box-shadow .2s ease",

    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },

    "&.Mui-focused": {
      boxShadow: "0 0 0 4px rgba(37,99,235,.09)",
    },
  },

  "& .MuiInputLabel-root": {
    fontWeight: 650,
  },
};

// API_BASE: https://.../api -> BACKEND_ORIGIN: https://...
const BACKEND_ORIGIN = API_BASE.replace(/\/api\/?$/, "");

/* ==========================================================================
   GÖRSEL URL YARDIMCILARI
   ========================================================================== */

const extractUploadsPath = (url) => {
  if (!url) return "";

  const value = String(url).trim();
  const match = value.match(/\/uploads\/[^\)"'\\]+/i);

  if (match) {
    return match[0].split("?")[0];
  }

  if (value.startsWith("uploads/")) {
    return `/${value.replace(/^\/+/, "").split("?")[0]}`;
  }

  return value.split("?")[0];
};

const toPublicUrl = (url) => {
  const path = extractUploadsPath(url);

  if (!path) return "";

  const relative = String(path).replace(/^\/?uploads\/?/, "");

  const encoded = relative
    .split("/")
    .map(encodeURIComponent)
    .join("/");

  return `${BACKEND_ORIGIN}/uploads/${encoded}`;
};

const PLACEHOLDER = "Bu dilde içerik mevcut değildir.";
const ALLOWED = ["tr", "en", "de"];

/* ==========================================================================
   DİL METİNLERİ
   ========================================================================== */

const T = {
  tr: {
    listTitle: "Ana Kategoriler",
    listSubtitle: "Uygulamanızın ana kategori yapısını yönetin",
    totalLabel: "Toplam kategori",

    newBtn: "Yeni Ana Kategori",
    exportBtn: "Dışa Aktar",

    save: "Kaydet",
    saving: "Kaydediliyor…",
    cancel: "Vazgeç",

    editTitle: "Ana Kategori Düzenle",
    editSubtitle: "Kategori bilgilerini ve yayın ayarlarını güncelleyin",
    updatedOk: "✅ Güncelleme başarılı",

    uploadImage: "Görsel Yükle",
    image: "Resim",
    title: "Başlık",
    description: "Açıklama",
    order: "Sıra",
    status: "Durum",
    contentLang: "İçerik Dili",
    contentLangShort: "İçerik Dili",

    edit: "Düzenle",
    delete: "Sil",
    operations: "İşlemler",
    confirmDelete: "Silmek istediğinize emin misiniz?",

    createTitle: "Yeni Ana Kategori Ekle",
    createSubtitle: "Mobil uygulamanız için yeni bir kategori oluşturun",
    createdOk: "✅ Ana kategori başarıyla oluşturuldu",

    imageUrl: "Görsel URL",
    active: "Aktif",
    passive: "Pasif",

    preview: "Canlı Önizleme",
    previewInfo: "Görsel ve içerik kartınız burada görünür.",

    formInfo: "Kategori Bilgileri",
    publishInfo: "Yayın ve Görsel Ayarları",

    loading: "Yükleniyor…",
    createError: "❌ Kayıt sırasında hata oluştu",
    serverError: "❌ Sunucu hatası",
    updateError: "❌ Güncelleme hatası",

    emptyTitle: "Ana kategoriler eklemeye hoş geldiniz",
    emptyDesc:
      "Buraya ekleyeceğiniz görseller ve metinler uygulamanızın ana ekranındaki kategoriler bölümünde yayınlanacaktır.",
    emptyBtn: "Hemen Ana Kategori Ekle",

    langNames: {
      tr: "Türkçe",
      en: "İngilizce",
      de: "Almanca",
    },
  },

  en: {
    listTitle: "Main Categories",
    listSubtitle: "Manage the main category structure of your application",
    totalLabel: "Total categories",

    newBtn: "New Main Category",
    exportBtn: "Export",

    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",

    editTitle: "Edit Main Category",
    editSubtitle: "Update category information and publishing settings",
    updatedOk: "✅ Updated successfully",

    uploadImage: "Upload Image",
    image: "Image",
    title: "Title",
    description: "Description",
    order: "Order",
    status: "Status",
    contentLang: "Content Language",
    contentLangShort: "Content Language",

    edit: "Edit",
    delete: "Delete",
    operations: "Actions",
    confirmDelete: "Are you sure you want to delete?",

    createTitle: "Create Main Category",
    createSubtitle: "Create a new category for your mobile application",
    createdOk: "✅ Main category created",

    imageUrl: "Image URL",
    active: "Active",
    passive: "Inactive",

    preview: "Live Preview",
    previewInfo: "Your image and content card appears here.",

    formInfo: "Category Information",
    publishInfo: "Publishing and Image Settings",

    loading: "Loading…",
    createError: "❌ An error occurred while saving",
    serverError: "❌ Server error",
    updateError: "❌ Update error",

    emptyTitle: "Welcome to adding main categories",
    emptyDesc:
      "Items you add here will appear in the main categories section of your app’s home screen.",
    emptyBtn: "Add Main Category",

    langNames: {
      tr: "Turkish",
      en: "English",
      de: "German",
    },
  },

  de: {
    listTitle: "Hauptkategorien",
    listSubtitle: "Verwalten Sie die Hauptkategorien Ihrer Anwendung",
    totalLabel: "Kategorien insgesamt",

    newBtn: "Neue Hauptkategorie",
    exportBtn: "Exportieren",

    save: "Speichern",
    saving: "Wird gespeichert…",
    cancel: "Abbrechen",

    editTitle: "Hauptkategorie bearbeiten",
    editSubtitle:
      "Kategorieinformationen und Veröffentlichung aktualisieren",
    updatedOk: "✅ Erfolgreich aktualisiert",

    uploadImage: "Bild hochladen",
    image: "Bild",
    title: "Titel",
    description: "Beschreibung",
    order: "Reihenfolge",
    status: "Status",
    contentLang: "Inhaltssprache",
    contentLangShort: "Inhaltssprache",

    edit: "Bearbeiten",
    delete: "Löschen",
    operations: "Aktionen",
    confirmDelete: "Möchten Sie wirklich löschen?",

    createTitle: "Hauptkategorie erstellen",
    createSubtitle:
      "Erstellen Sie eine neue Kategorie für Ihre mobile Anwendung",
    createdOk: "✅ Hauptkategorie erstellt",

    imageUrl: "Bild-URL",
    active: "Aktiv",
    passive: "Inaktiv",

    preview: "Live-Vorschau",
    previewInfo: "Ihre Bild- und Inhaltskarte wird hier angezeigt.",

    formInfo: "Kategorieinformationen",
    publishInfo: "Veröffentlichungs- und Bildeinstellungen",

    loading: "Wird geladen…",
    createError: "❌ Beim Speichern ist ein Fehler aufgetreten",
    serverError: "❌ Serverfehler",
    updateError: "❌ Aktualisierungsfehler",

    emptyTitle: "Willkommen zum Hinzufügen von Hauptkategorien",
    emptyDesc:
      "Elemente, die Sie hier hinzufügen, erscheinen im Bereich der Hauptkategorien auf der Startseite Ihrer App.",
    emptyBtn: "Hauptkategorie hinzufügen",

    langNames: {
      tr: "Türkisch",
      en: "Englisch",
      de: "Deutsch",
    },
  },
};

/* ==========================================================================
   DİL KONTROLÜ
   ========================================================================== */

function useUiTexts() {
  const [uiLang, setUiLang] = useState(() =>
    (localStorage.getItem("app_lang") || "tr")
      .slice(0, 2)
      .toLowerCase(),
  );

  useEffect(() => {
    const sync = () => {
      const nextLang = (localStorage.getItem("app_lang") || "tr")
        .slice(0, 2)
        .toLowerCase();

      setUiLang(ALLOWED.includes(nextLang) ? nextLang : "tr");
    };

    window.addEventListener("app_lang_change", sync);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);

    return () => {
      window.removeEventListener("app_lang_change", sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  return useMemo(
    () => T[ALLOWED.includes(uiLang) ? uiLang : "tr"],
    [uiLang],
  );
}

/* ==========================================================================
   KAYIT YARDIMCILARI
   ========================================================================== */

function pickRecordTitle(record) {
  if (!record) return "—";

  const pick = (code) =>
    code === "tr"
      ? record.baslik_tr
      : code === "en"
        ? record.baslik_en
        : record.baslik_de;

  const order = [record.dil, "tr", "en", "de"].filter(Boolean);

  for (const code of order) {
    const value = pick(code);

    if (
      value &&
      value.trim() &&
      value.trim() !== PLACEHOLDER
    ) {
      return value;
    }
  }

  return "—";
}

function pickRecordDescription(record) {
  if (!record) return "";

  const pick = (code) =>
    code === "tr"
      ? record.aciklama_tr
      : code === "en"
        ? record.aciklama_en
        : record.aciklama_de;

  const order = [record.dil, "tr", "en", "de"].filter(Boolean);

  for (const code of order) {
    const value = pick(code);

    if (
      value &&
      value.trim() &&
      value.trim() !== PLACEHOLDER
    ) {
      return value;
    }
  }

  return "";
}

function getRecordLanguage(record) {
  if (!record) return "tr";

  if (ALLOWED.includes(record.dil)) {
    return record.dil;
  }

  if (
    record.baslik_tr &&
    record.baslik_tr !== PLACEHOLDER
  ) {
    return "tr";
  }

  if (
    record.baslik_en &&
    record.baslik_en !== PLACEHOLDER
  ) {
    return "en";
  }

  if (
    record.baslik_de &&
    record.baslik_de !== PLACEHOLDER
  ) {
    return "de";
  }

  return "tr";
}

/* ==========================================================================
   ÜST İŞLEM BUTONLARI
   ========================================================================== */

const ListActions = () => {
  const TT = useUiTexts();

  return (
    <TopToolbar
      sx={{
        width: "100%",

        px: {
          xs: 1.5,
          sm: 0,
        },

        py: 1.25,
        gap: 1,
        justifyContent: "flex-end",
        flexWrap: "wrap",
      }}
    >
      <CreateButton
        icon={<AddCircleIcon />}
        label={TT.newBtn}
        sx={{
          minHeight: 42,

          px: {
            xs: 1.5,
            sm: 2.5,
          },

          borderRadius: 2.5,
          textTransform: "none",
          fontWeight: 850,
          bgcolor: `${COLORS.orange} !important`,
          color: `${COLORS.navy} !important`,
          boxShadow: "0 8px 20px rgba(245,158,11,.22)",

          "&:hover": {
            bgcolor: "#fbbf24 !important",
          },
        }}
      />

      <ExportButton
        icon={<FileDownloadIcon />}
        label={TT.exportBtn}
        sx={{
          minHeight: 42,

          px: {
            xs: 1.5,
            sm: 2.25,
          },

          borderRadius: 2.5,
          textTransform: "none",
          fontWeight: 800,
          bgcolor: `${COLORS.blue} !important`,
          color: "#fff !important",
          boxShadow: "0 8px 20px rgba(37,99,235,.18)",

          "&:hover": {
            bgcolor: "#1d4ed8 !important",
          },
        }}
      />
    </TopToolbar>
  );
};

/* ==========================================================================
   SİLME BUTONU
   DELETE ENDPOINT AYNEN KORUNDU
   ========================================================================== */

const CustomDeleteButton = ({
  record,
  compact = false,
}) => {
  const TT = useUiTexts();
  const refresh = useRefresh();

  const handleDelete = async (event) => {
    event?.stopPropagation?.();

    const confirmed = window.confirm(TT.confirmDelete);

    if (!confirmed) return;

    const token = localStorage.getItem("token");

    await fetch(
      `${API_BASE}/anakategoriler/${record.id}`,
      {
        method: "DELETE",

        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    refresh();
  };

  if (compact) {
    return (
      <Tooltip title={TT.delete}>
        <IconButton
          color="error"
          onClick={handleDelete}
          sx={{
            bgcolor: "#fff1f2",

            "&:hover": {
              bgcolor: "#ffe4e6",
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      onClick={handleDelete}
      startIcon={<DeleteIcon />}
      color="error"
      variant="contained"
      size="small"
      sx={{
        minHeight: 38,
        borderRadius: 2,
        textTransform: "none",
        fontWeight: 800,
        boxShadow: "none",
      }}
    >
      {TT.delete}
    </Button>
  );
};

/* ==========================================================================
   DÜZENLE / SİL BUTONLARI
   ========================================================================== */

const ActionButtons = ({
  compact = false,
  record: recordProp,
}) => {
  const TT = useUiTexts();
  const contextRecord = useRecordContext();
  const record = recordProp || contextRecord;

  if (!record) return null;

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
    >
      <EditButton
        record={record}
        icon={<EditIcon />}
        label={compact ? "" : TT.edit}
        onClick={(event) => event.stopPropagation()}
        sx={{
          minWidth: compact ? 42 : "auto",
          minHeight: 38,
          px: compact ? 0.75 : 1.5,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 800,
          bgcolor: `${COLORS.orangeSoft} !important`,
          color: "#b45309 !important",

          "& .MuiButton-startIcon": {
            mr: compact ? 0 : 1,
          },

          "&:hover": {
            bgcolor: "#ffedd5 !important",
          },
        }}
      />

      <CustomDeleteButton
        record={record}
        compact={compact}
      />
    </Stack>
  );
};

/* ==========================================================================
   TABLO ALANLARI
   ========================================================================== */

const BaslikField = ({ label }) => {
  const record = useRecordContext();

  if (!record) return null;

  const value = pickRecordTitle(record);
  const description = pickRecordDescription(record);

  return (
    <Box
      aria-label={label}
      title={value}
      sx={{
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          color: COLORS.text,
          lineHeight: 1.35,
        }}
      >
        {value}
      </Typography>

      {description && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.4,
            color: COLORS.textSoft,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 440,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

const DilField = ({
  label,
  names,
}) => {
  const record = useRecordContext();

  if (!record) return null;

  const code = getRecordLanguage(record);

  return (
    <Chip
      icon={<LanguageIcon />}
      label={names[code] || "—"}
      aria-label={label}
      size="small"
      sx={{
        fontWeight: 800,
        color: COLORS.navy,
        bgcolor: COLORS.blueSoft,

        "& .MuiChip-icon": {
          color: COLORS.blue,
        },
      }}
    />
  );
};

const StatusField = () => {
  const record = useRecordContext();
  const TT = useUiTexts();

  if (!record) return null;

  return (
    <Chip
      icon={
        record.aktif
          ? <CheckCircleIcon />
          : undefined
      }
      label={
        record.aktif
          ? TT.active
          : TT.passive
      }
      size="small"
      sx={{
        fontWeight: 850,

        color: record.aktif
          ? "#047857"
          : COLORS.textSoft,

        bgcolor: record.aktif
          ? "#ecfdf5"
          : "#f1f5f9",

        "& .MuiChip-icon": {
          color: COLORS.green,
        },
      }}
    />
  );
};

const ImageField = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: 2.5,
        overflow: "hidden",
        border: `1px solid ${COLORS.border}`,
        bgcolor: "#f1f5f9",
        display: "grid",
        placeItems: "center",
      }}
    >
      {record.resim ? (
        <Box
          component="img"
          src={toPublicUrl(record.resim)}
          alt={pickRecordTitle(record)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <ImageIcon
          sx={{
            color: "#94a3b8",
          }}
        />
      )}
    </Box>
  );
};

/* ==========================================================================
   BOŞ LİSTE
   ========================================================================== */

const EmptyState = () => {
  const TT = useUiTexts();

  return (
    <Box
      sx={{
        minHeight: "55vh",
        display: "grid",
        placeItems: "center",
        p: 2,
        bgcolor: COLORS.page,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 680,

          p: {
            xs: 3,
            sm: 5,
          },

          borderRadius: 4,
          borderStyle: "dashed",
          borderColor: "#cbd5e1",
          textAlign: "center",
          boxShadow: "0 16px 40px rgba(15,23,42,.05)",
        }}
      >
        <Box
          sx={{
            width: 82,
            height: 82,
            borderRadius: 3,
            bgcolor: COLORS.blueSoft,
            color: COLORS.blue,
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <CategoryIcon
            sx={{
              fontSize: 42,
            }}
          />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 950,
            color: COLORS.text,
          }}
        >
          {TT.emptyTitle}
        </Typography>

        <Typography
          sx={{
            mt: 1.25,
            color: COLORS.textSoft,
            lineHeight: 1.7,
          }}
        >
          {TT.emptyDesc}
        </Typography>

        <CreateButton
          label={TT.emptyBtn}
          icon={<AddCircleIcon />}
          sx={{
            mt: 2.5,
            minHeight: 46,
            px: 3,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 850,
            bgcolor: `${COLORS.orange} !important`,
            color: `${COLORS.navy} !important`,
          }}
        />
      </Paper>
    </Box>
  );
};

/* ==========================================================================
   LİSTE BAŞLIĞI
   ========================================================================== */

function ListHeader() {
  const TT = useUiTexts();
  const { total, data } = useListContext();

  const count = total ?? data?.length ?? 0;

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          sm: 3,
        },

        color: "#fff",

        background:
          "linear-gradient(125deg, #071a33 0%, #123b6d 58%, #1f5aa6 100%)",

        borderBottom: `4px solid ${COLORS.orange}`,
        position: "relative",
        overflow: "hidden",

        "&::after": {
          content: '""',
          position: "absolute",
          width: 220,
          height: 220,
          borderRadius: "50%",
          right: -100,
          top: -150,
          bgcolor: "rgba(255,255,255,.1)",
        },
      }}
    >
      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        alignItems={{
          xs: "flex-start",
          sm: "center",
        }}
        justifyContent="space-between"
        spacing={2}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={1.75}
          alignItems="center"
        >
          <Box
            sx={{
              width: {
                xs: 48,
                sm: 56,
              },

              height: {
                xs: 48,
                sm: 56,
              },

              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              color: "#fbbf24",
              bgcolor: "rgba(255,255,255,.11)",
              border: "1px solid rgba(255,255,255,.18)",
              flexShrink: 0,
            }}
          >
            <CategoryIcon fontSize="large" />
          </Box>

          <Box>
            <Typography
              component="h1"
              sx={{
                fontSize: {
                  xs: "1.55rem",
                  sm: "2rem",
                },

                fontWeight: 950,
              }}
            >
              {TT.listTitle}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.4,
                color: "rgba(255,255,255,.76)",
              }}
            >
              {TT.listSubtitle}
            </Typography>
          </Box>
        </Stack>

        <Chip
          label={`${TT.totalLabel}: ${count}`}
          sx={{
            color: "#fff",
            fontWeight: 850,
            bgcolor: "rgba(255,255,255,.12)",
            border: "1px solid rgba(255,255,255,.2)",
          }}
        />
      </Stack>
    </Box>
  );
}

/* ==========================================================================
   MOBİL KATEGORİ KARTI
   ========================================================================== */

function MobileCategoryCard({ record }) {
  const TT = useUiTexts();

  const language = getRecordLanguage(record);
  const title = pickRecordTitle(record);
  const description = pickRecordDescription(record);

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        borderRadius: 3.5,
        borderColor: COLORS.border,
        boxShadow: "0 10px 28px rgba(15,23,42,.055)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: 5,

          background: record.aktif
            ? "linear-gradient(90deg, #10b981, #34d399)"
            : "linear-gradient(90deg, #94a3b8, #cbd5e1)",
        }}
      />

      <CardContent
        sx={{
          p: 2,

          "&:last-child": {
            pb: 2,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="flex-start"
        >
          <Box
            sx={{
              width: 78,
              height: 78,
              borderRadius: 3,
              overflow: "hidden",
              flexShrink: 0,
              bgcolor: "#f1f5f9",
              border: `1px solid ${COLORS.border}`,
              display: "grid",
              placeItems: "center",
            }}
          >
            {record.resim ? (
              <Box
                component="img"
                src={toPublicUrl(record.resim)}
                alt={title}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <ImageIcon
                sx={{
                  fontSize: 34,
                  color: "#94a3b8",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontWeight: 950,
                color: COLORS.text,
                fontSize: "1.02rem",
                overflowWrap: "anywhere",
              }}
            >
              {title}
            </Typography>

            {description && (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: COLORS.textSoft,
                  lineHeight: 1.55,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {description}
              </Typography>
            )}
          </Box>
        </Stack>

        <Divider
          sx={{
            my: 1.75,
            borderColor: COLORS.border,
          }}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              size="small"
              label={TT.langNames[language]}
              icon={<LanguageIcon />}
              sx={{
                fontWeight: 800,
                bgcolor: COLORS.blueSoft,

                "& .MuiChip-icon": {
                  color: COLORS.blue,
                },
              }}
            />

            <Chip
              size="small"
              label={`${TT.order}: ${record.sira ?? "—"}`}
              icon={<SortIcon />}
              sx={{
                fontWeight: 800,
                bgcolor: "#f8fafc",
              }}
            />

            <Chip
              size="small"
              label={
                record.aktif
                  ? TT.active
                  : TT.passive
              }
              sx={{
                fontWeight: 850,

                color: record.aktif
                  ? "#047857"
                  : COLORS.textSoft,

                bgcolor: record.aktif
                  ? "#ecfdf5"
                  : "#f1f5f9",
              }}
            />
          </Stack>

          <ActionButtons
            compact
            record={record}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

/* ==========================================================================
   MOBİL VE MASAÜSTÜ LİSTE
   ========================================================================== */

function ResponsiveCategoryList() {
  const TT = useUiTexts();
  const { data = [] } = useListContext();

  return (
    <>
      {/* MOBİL KARTLAR */}

      <Box
        sx={{
          display: {
            xs: "block",
            lg: "none",
          },

          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          p: 1.5,
        }}
      >
        <Stack spacing={1.5}>
          {data.map((record) => (
            <Box
              key={record.id}
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
              }}
            >
              <MobileCategoryCard record={record} />
            </Box>
          ))}
        </Stack>
      </Box>

      {/* MASAÜSTÜ TABLO */}

      <Box
        sx={{
          display: {
            xs: "none",
            lg: "block",
          },

          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",

          "&::-webkit-scrollbar": {
            height: 8,
          },

          "&::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            bgcolor: "#cbd5e1",
          },
        }}
      >
        <Datagrid
          rowClick="edit"
          bulkActionButtons={false}
          sx={{
            width: "100%",
            minWidth: 650,
            tableLayout: "fixed",

            "& .RaDatagrid-headerCell": {
              py: 1.6,
              bgcolor: "#f8fafc",
              color: COLORS.textSoft,
              fontWeight: 900,
              fontSize: ".78rem",
              textTransform: "uppercase",
              letterSpacing: 0.45,
              borderBottom: `1px solid ${COLORS.border}`,
            },

            "& .RaDatagrid-row": {
              transition: "background-color .2s ease",

              "&:hover": {
                bgcolor: "#f8fafc",
              },
            },

            "& .MuiTableCell-root": {
              py: 1.25,
              px: 1.5,
              verticalAlign: "middle",
              borderBottom: `1px solid ${COLORS.border}`,
            },

            "& .column-resim": {
              width: 76,
              minWidth: 76,
            },

            "& .column-baslik": {
              width: "auto",
            },

            "& .column-sira": {
              width: 84,
              minWidth: 84,
            },

            "& .column-aktif": {
              width: 86,
              minWidth: 86,
            },

            "& .column-lang": {
              width: 104,
              minWidth: 104,
            },

            "& .column-actions": {
              position: "sticky",
              right: 0,
              zIndex: 3,
              width: 174,
              minWidth: 174,
              bgcolor: "#fff",
              boxShadow: "-8px 0 14px rgba(15,23,42,.06)",
            },

            "& .RaDatagrid-headerCell.column-actions": {
              zIndex: 5,
              bgcolor: "#f8fafc",
            },

            "& .column-actions .MuiStack-root": {
              width: "100%",
              justifyContent: "flex-end",
              gap: 0.5,
            },

            "& .column-actions .MuiButton-root": {
              minWidth: 0,
              px: 1.15,
            },

            "@media (max-width: 1350px)": {
              minWidth: 600,

              "& .column-lang": {
                display: "none",
              },
            },
          }}
        >
          <FunctionField
            label={TT.image}
            className="column-resim"
            render={() => <ImageField />}
          />

          <FunctionField
            label={TT.title}
            className="column-baslik"
            render={() => (
              <BaslikField label={TT.title} />
            )}
          />

          <FunctionField
            label={TT.order}
            className="column-sira"
            render={(record) => (
              <Chip
                size="small"
                icon={<SortIcon />}
                label={record?.sira ?? "—"}
                sx={{
                  fontWeight: 850,
                  bgcolor: "#f8fafc",
                }}
              />
            )}
          />

          <FunctionField
            label={TT.status}
            className="column-aktif"
            render={() => <StatusField />}
          />

          <FunctionField
            label={TT.contentLang}
            className="column-lang"
            render={() => (
              <DilField
                label={TT.contentLang}
                names={TT.langNames}
              />
            )}
          />

          <FunctionField
            label={TT.operations}
            className="column-actions"
            render={() => <ActionButtons />}
          />
        </Datagrid>
      </Box>
    </>
  );
}

/* ==========================================================================
   LİSTE SAYFASI
   ========================================================================== */

export const AnaKategorilerPage = (props) => {
  const TT = useUiTexts();

  return (
    <>
      <PageGlobalStyles />

      <List
        {...props}
        resource="anakategoriler"
        title={TT.listTitle}
        actions={<ListActions />}
        sort={{
          field: "sira",
          order: "ASC",
        }}
        empty={<EmptyState />}
        sx={{
          m: 0,
          p: 0,
          width: "100%",
          maxWidth: "100%",
          bgcolor: COLORS.page,

          "& .RaList-main": {
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          },

          "& .RaList-content": {
            m: 0,
            p: 0,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",

            borderRadius: {
              xs: 0,
              md: 3.5,
            },

            border: {
              md: `1px solid ${COLORS.border}`,
            },

            boxShadow: {
              md: "0 18px 48px rgba(15,23,42,.07)",
            },
          },

          "& .MuiCard-root": {
            boxShadow: "none",
          },

          "& .MuiContainer-root": {
            maxWidth: "100% !important",
            px: "0 !important",
          },
        }}
      >
        <Box>
          <ListHeader />
          <ResponsiveCategoryList />
        </Box>
      </List>
    </>
  );
};

/* ==========================================================================
   GLOBAL TASARIM
   ========================================================================== */

function PageGlobalStyles() {
  return (
    <GlobalStyles
      styles={{
        html: {
          overflowX: "hidden",
        },

        body: {
          overflowX: "hidden",
          background: COLORS.page,
        },

        "*": {
          boxSizing: "border-box",
        },

        ".MuiSnackbarContent-message": {
          fontSize: "1rem",
          fontWeight: 800,
        },

        ".RaNotification-content": {
          fontSize: "1rem",
          fontWeight: 800,
        },
      }}
    />
  );
}

/* ==========================================================================
   FORM SAYFASI BAŞLIĞI
   ========================================================================== */

function FormPageHeader({
  title,
  subtitle,
  TT,
  onBack,
}) {
  return (
    <Box
      sx={{
        p: {
          xs: 2.25,
          sm: 3.5,
        },

        color: "#fff",

        background:
          "linear-gradient(125deg, #071a33 0%, #123b6d 58%, #1f5aa6 100%)",

        borderBottom: `4px solid ${COLORS.orange}`,
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
      >
        <Tooltip title={TT.cancel}>
          <IconButton
            onClick={onBack}
            sx={{
              color: "#fff",
              bgcolor: "rgba(255,255,255,.12)",
              border: "1px solid rgba(255,255,255,.18)",

              "&:hover": {
                bgcolor: "rgba(255,255,255,.2)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: "1.45rem",
                sm: "2rem",
              },

              fontWeight: 950,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.35,
              color: "rgba(255,255,255,.76)",
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/* ==========================================================================
   FORM BÖLÜM BAŞLIĞI
   ========================================================================== */

function FormSectionTitle({
  icon,
  title,
  subtitle,
}) {
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{
        mb: 2.25,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          bgcolor: COLORS.blueSoft,
          color: COLORS.blue,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography
          sx={{
            fontWeight: 950,
            color: COLORS.text,
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: COLORS.textSoft,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

/* ==========================================================================
   ORTAK KATEGORİ FORMU
   ========================================================================== */

function CategoryForm({
  form,
  setForm,
  onSubmit,
  saving,
  active,
  onActiveChange,
  TT,
  onCancel,
}) {
  const titleKey = `baslik_${form.dil}`;
  const descriptionKey = `aciklama_${form.dil}`;
  const previewUrl = toPublicUrl(form.resim);

  const setValue = (name, value) => {
    setForm((previous) => ({
      ...previous,
      [name]:
        name === "sira"
          ? Number(value)
          : value,
    }));
  };

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 7fr) minmax(0, 5fr)",
          },
          gap: 2.5,
          alignItems: "stretch",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        {/* KATEGORİ BİLGİLERİ */}

        <Box
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              width: "100%",

              p: {
                xs: 2,
                sm: 3,
              },

              borderRadius: 4,
              borderColor: COLORS.border,
              boxShadow: "0 12px 34px rgba(15,23,42,.05)",
            }}
          >
            <FormSectionTitle
              icon={<CategoryIcon />}
              title={TT.formInfo}
              subtitle={TT.contentLang}
            />

            <Stack spacing={2.25}>
              <MuiTextField
                select
                fullWidth
                label={TT.contentLang}
                name="dil"
                value={form.dil}
                onChange={(event) =>
                  setValue(
                    "dil",
                    event.target.value,
                  )
                }
                sx={fieldSx}
              >
                {ALLOWED.map((code) => (
                  <MenuItem
                    key={code}
                    value={code}
                  >
                    {TT.langNames[code]}
                  </MenuItem>
                ))}
              </MuiTextField>

              <MuiTextField
                fullWidth
                required
                label={TT.title}
                name={titleKey}
                value={form[titleKey] || ""}
                onChange={(event) =>
                  setValue(
                    titleKey,
                    event.target.value,
                  )
                }
                sx={fieldSx}
              />

              <MuiTextField
                fullWidth
                required
                multiline
                minRows={7}
                maxRows={14}
                label={TT.description}
                name={descriptionKey}
                value={form[descriptionKey] || ""}
                onChange={(event) =>
                  setValue(
                    descriptionKey,
                    event.target.value,
                  )
                }
                sx={fieldSx}
              />
            </Stack>
          </Paper>
        </Box>

        {/* YAYIN VE GÖRSEL AYARLARI */}

        <Box
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              width: "100%",

              p: {
                xs: 2,
                sm: 3,
              },

              borderRadius: 4,
              borderColor: COLORS.border,
              boxShadow: "0 12px 34px rgba(15,23,42,.05)",
            }}
          >
            <FormSectionTitle
              icon={<ImageIcon />}
              title={TT.publishInfo}
              subtitle={
                active
                  ? TT.active
                  : TT.passive
              }
            />

            <Stack spacing={2.25}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  borderColor: COLORS.border,
                  bgcolor: "#f8fafc",
                  overflow: "hidden",

                  "& button": {
                    maxWidth: "100%",
                  },
                }}
              >
                <ResimUpload
                  label={TT.uploadImage}
                  defaultPreview={previewUrl}
                  onUpload={(url) =>
                    setValue(
                      "resim",
                      extractUploadsPath(url),
                    )
                  }
                />
              </Paper>

              <MuiTextField
                fullWidth
                type="number"
                label={TT.order}
                name="sira"
                value={form.sira}
                onChange={(event) =>
                  setValue(
                    "sira",
                    event.target.value,
                  )
                }
                inputProps={{
                  min: 0,
                }}
                sx={fieldSx}
              />

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  borderColor: COLORS.border,

                  bgcolor: active
                    ? "#ecfdf5"
                    : "#f8fafc",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={active}
                      onChange={(event) =>
                        onActiveChange(
                          event.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    active
                      ? TT.active
                      : TT.passive
                  }
                  sx={{
                    m: 0,
                    width: "100%",

                    "& .MuiFormControlLabel-label": {
                      fontWeight: 850,

                      color: active
                        ? "#047857"
                        : COLORS.textSoft,
                    },
                  }}
                />
              </Paper>

              <Divider
                sx={{
                  borderColor: COLORS.border,
                }}
              />

              <Box>
                <Typography
                  sx={{
                    fontWeight: 900,
                    color: COLORS.text,
                  }}
                >
                  {TT.preview}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: COLORS.textSoft,
                  }}
                >
                  {TT.previewInfo}
                </Typography>
              </Box>

              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3.5,
                  borderColor: COLORS.border,
                  overflow: "hidden",
                  boxShadow: "0 10px 28px rgba(15,23,42,.06)",
                }}
              >
                {previewUrl ? (
                  <Box
                    component="img"
                    src={previewUrl}
                    alt={
                      form[titleKey] ||
                      TT.title
                    }
                    sx={{
                      width: "100%",
                      height: 190,
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 190,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "#e2e8f0",
                      color: "#94a3b8",
                    }}
                  >
                    <ImageIcon
                      sx={{
                        fontSize: 54,
                      }}
                    />
                  </Box>
                )}

                <CardContent>
                  <Typography
                    sx={{
                      fontWeight: 950,
                      color: COLORS.text,
                    }}
                  >
                    {form[titleKey] || TT.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.6,
                      color: COLORS.textSoft,
                      lineHeight: 1.6,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {form[descriptionKey] || TT.description}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{
                      mt: 1.5,
                    }}
                  >
                    <Chip
                      size="small"
                      label={TT.langNames[form.dil]}
                      sx={{
                        fontWeight: 800,
                        bgcolor: COLORS.blueSoft,
                      }}
                    />

                    <Chip
                      size="small"
                      label={`${TT.order}: ${form.sira ?? 0}`}
                      sx={{
                        fontWeight: 800,
                        bgcolor: "#f8fafc",
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Paper>
        </Box>
      </Box>

      {/* FORM BUTONLARI */}

      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: 3,
          border: `1px solid ${COLORS.border}`,
          bgcolor: "#fff",
        }}
      >
        <Stack
          direction={{
            xs: "column-reverse",
            sm: "row",
          }}
          justifyContent="flex-end"
          spacing={1.25}
        >
          <Button
            type="button"
            onClick={onCancel}
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            sx={{
              minHeight: 48,
              px: 3,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 850,
              borderColor: COLORS.border,
              color: COLORS.text,
            }}
          >
            {TT.cancel}
          </Button>

          <Button
            type="submit"
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress
                  size={18}
                  color="inherit"
                />
              ) : (
                <SaveIcon />
              )
            }
            variant="contained"
            sx={{
              minHeight: 48,
              px: 3.5,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 900,
              bgcolor: COLORS.navy,
              boxShadow: "0 10px 25px rgba(11,31,58,.2)",

              "&:hover": {
                bgcolor: COLORS.navySoft,
              },
            }}
          >
            {saving
              ? TT.saving
              : TT.save}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

/* ==========================================================================
   FORM SAYFASI DIŞ YAPISI
   ========================================================================== */

function FormPageShell({
  title,
  subtitle,
  TT,
  onBack,
  children,
}) {
  return (
    <>
      <PageGlobalStyles />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: COLORS.page,
          pb: 5,
        }}
      >
        <FormPageHeader
          title={title}
          subtitle={subtitle}
          TT={TT}
          onBack={onBack}
        />

        <Container
          maxWidth="xl"
          sx={{
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",

            mt: {
              xs: 2,
              md: 3,
            },

            px: {
              xs: 1.5,
              sm: 3,
            },
          }}
        >
          {children}
        </Container>
      </Box>
    </>
  );
}

/* ==========================================================================
   DÜZENLEME SAYFASI
   ========================================================================== */

export const AnaKategoriEdit = (props) => (
  <EditBase
    resource="anakategoriler"
    {...props}
  >
    <AnaKategoriEditView />
  </EditBase>
);

function AnaKategoriEditView() {
  const TT = useUiTexts();
  const notify = useNotify();
  const redirect = useRedirect();

  const {
    record,
    isLoading,
  } = useEditContext();

  const [
    update,
    {
      isLoading: saving,
    },
  ] = useUpdate();

  const [form, setForm] = React.useState({
    baslik_tr: "",
    baslik_en: "",
    baslik_de: "",

    aciklama_tr: "",
    aciklama_en: "",
    aciklama_de: "",

    resim: "",
    link: "",
    aktif: true,
    dil: "tr",
    sira: 0,
  });

  React.useEffect(() => {
    if (!isLoading && record) {
      setForm({
        baslik_tr: record.baslik_tr || "",
        baslik_en: record.baslik_en || "",
        baslik_de: record.baslik_de || "",

        aciklama_tr: record.aciklama_tr || "",
        aciklama_en: record.aciklama_en || "",
        aciklama_de: record.aciklama_de || "",

        resim: extractUploadsPath(record.resim || ""),
        link: record.link || "",
        aktif: !!record.aktif,
        dil: record.dil || "tr",
        sira: Number(record.sira) || 0,
      });
    }
  }, [isLoading, record]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!record) return;

    const merged = {
      ...record,
      ...form,

      resim: extractUploadsPath(form.resim),
      aktif: !!form.aktif,
      sira: Number(form.sira) || 0,
    };

    update(
      "anakategoriler",

      {
        id: record.id,
        data: merged,
        previousData: record,
      },

      {
        onSuccess: () => {
          notify(
            TT.updatedOk,
            {
              type: "success",
            },
          );

          redirect("/anakategoriler");
        },

        onError: (error) =>
          notify(
            `${TT.updateError}: ${error?.message || ""}`,

            {
              type: "warning",
            },
          ),
      },
    );
  };

  if (
    isLoading ||
    !record
  ) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "grid",
          placeItems: "center",
          bgcolor: COLORS.page,
        }}
      >
        <Stack
          alignItems="center"
          spacing={1.5}
        >
          <CircularProgress />

          <Typography color="text.secondary">
            {TT.loading}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <FormPageShell
      title={TT.editTitle}
      subtitle={TT.editSubtitle}
      TT={TT}
      onBack={() =>
        redirect("/anakategoriler")
      }
    >
      <CategoryForm
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        saving={saving}
        active={!!form.aktif}
        onActiveChange={(checked) =>
          setForm((previous) => ({
            ...previous,
            aktif: checked,
          }))
        }
        TT={TT}
        onCancel={() =>
          redirect("/anakategoriler")
        }
      />
    </FormPageShell>
  );
}

/* ==========================================================================
   YENİ KATEGORİ OLUŞTURMA
   POST ENDPOINT VE PAYLOAD KORUNDU
   ========================================================================== */

const AnaKategorilerCard = () => {
  const TT = useUiTexts();
  const notify = useNotify();
  const redirect = useRedirect();

  const [saving, setSaving] = React.useState(false);

  const [formData, setFormData] = React.useState({
    baslik_tr: "",
    baslik_en: "",
    baslik_de: "",

    aciklama_tr: "",
    aciklama_en: "",
    aciklama_de: "",

    resim: "",
    durum: "Aktif",
    dil: "tr",
    sira: 0,
  });

  useEffect(() => {
    const appLang = (
      localStorage.getItem("app_lang") ||
      "tr"
    )
      .slice(0, 2)
      .toLowerCase();

    if (!ALLOWED.includes(appLang)) {
      return;
    }

    setFormData((previous) => ({
      ...previous,
      dil: appLang,
    }));
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const aktif =
      formData.durum === "Aktif";

    const payload = {
      baslik_tr:
        formData.dil === "tr"
          ? formData.baslik_tr
          : PLACEHOLDER,

      baslik_en:
        formData.dil === "en"
          ? formData.baslik_en
          : PLACEHOLDER,

      baslik_de:
        formData.dil === "de"
          ? formData.baslik_de
          : PLACEHOLDER,

      aciklama_tr:
        formData.dil === "tr"
          ? formData.aciklama_tr
          : PLACEHOLDER,

      aciklama_en:
        formData.dil === "en"
          ? formData.aciklama_en
          : PLACEHOLDER,

      aciklama_de:
        formData.dil === "de"
          ? formData.aciklama_de
          : PLACEHOLDER,

      resim: extractUploadsPath(formData.resim),
      aktif,
      dil: formData.dil,
      sira: Number(formData.sira) || 0,
    };

    try {
      setSaving(true);

      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE}/anakategoriler`,

        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },

          body: JSON.stringify(payload),
        },
      );

      if (response.ok) {
        notify(
          TT.createdOk,
          {
            type: "success",
          },
        );

        redirect("/anakategoriler");
      } else {
        notify(
          TT.createError,
          {
            type: "error",
          },
        );
      }
    } catch (error) {
      console.error(
        "Sunucu hatası:",
        error,
      );

      notify(
        TT.serverError,
        {
          type: "error",
        },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPageShell
      title={TT.createTitle}
      subtitle={TT.createSubtitle}
      TT={TT}
      onBack={() =>
        redirect("/anakategoriler")
      }
    >
      <CategoryForm
        form={formData}
        setForm={setFormData}
        onSubmit={handleSubmit}
        saving={saving}
        active={formData.durum === "Aktif"}
        onActiveChange={(checked) =>
          setFormData((previous) => ({
            ...previous,

            durum: checked
              ? "Aktif"
              : "Pasif",
          }))
        }
        TT={TT}
        onCancel={() =>
          redirect("/anakategoriler")
        }
      />
    </FormPageShell>
  );
};

export const AnaKategoriCreate = () => (
  <AnaKategorilerCard />
);
